import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { BlockNoteEditor } from '@blocknote/core';
// F 217 — neighbour-aware block spacing (Option A).
import { neighbourSpacing } from '../../../core/editor/neighbourSpacingExtension';
import { en } from '@blocknote/core/locales';
import { BlockNoteView } from '@blocknote/mantine';
import { createCodeBlockSpec, defaultBlockSpecs, BlockNoteSchema } from '@blocknote/core/blocks';
// F 232 — code block chrome. Lives HERE, not in the common BlockNoteEditor
// wrapper: that component is not mounted anywhere in this app (measured —
// `[data-fc-blocknote-editor]` count 0 in a rendered page), so the first
// attempt configured an editor that never existed. This is the only call
// site that actually builds the editor.
import { attachCodeChrome } from '../common/CodeBlockChrome';
import { getCodeHighlighter } from '../../../core/editor/codeHighlighter';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../../../core/editor/codeLanguages';
import { RawMarkdownEditor } from '../../primitives/RawMarkdownEditor';
// F 154 C2 — paste normalisation. Intervenes only for structured sources;
// plain text is left to BlockNote's own handler.
import { usePasteNormalisation } from '../../../core/editor/paste-blocknote';
import {
  Box, Group, ActionIcon, TextInput, Text, Notification,
} from '@mantine/core';
import {
  IconArrowLeft, IconTrash, IconFileText, IconBold, IconItalic,
  IconH1, IconH2, IconCheck, IconCircleCheck, IconCode,
} from '@tabler/icons-react';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
// F 371 — moving a block to another page. The rule lives in core so it can
// be tested without a DOM; see src/core/editor/moveBlock.ts.
import { moveBlockToPage } from '../../../core/editor/moveBlock';
// F 392 — the inline-database block: a database nested inside a page, the way
// Notion does it. The block type lives beside the database components; the
// registration happens in the schema below, next to codeBlock.
import { inlineDatabaseBlock } from '../database/inlineDatabaseBlock';
import { getInlineDatabaseSlashMenuItems } from '../database/inlineDatabaseSuggestion';
import { getEncryptedBlockSlashMenuItems } from '../database/encryptedBlockSuggestion';
import '../database/InlineDatabase.css';

/**
 * F 392 — this editor's block schema is the DEFAULT specs PLUS `codeBlock`
 * (F 232) and `inlineDatabase` (F 392), so the editor it creates is NOT the
 * bare `BlockNoteEditor` type.
 *
 * The rest of this file (replaceBlocks, removeBlocks, document, ~90 members)
 * is written against the DEFAULT schema type, and retyping all of it is well
 * outside "do not change the core engine". So the widening is confined to the
 * ONE boundary where the custom schema is handed over, and the value is
 * otherwise untouched — the runtime editor has exactly the same members as
 * before; only the static type was narrow. F 232's codeBlock had the same
 * shape of mismatch and was carried the same way.
 */
type FCCustomSchemaEditor = BlockNoteEditor;

// F 196A — the one rule that makes the editor fill its space. Scoped to
// .fc-page-editor so it cannot resize DatabaseView's BlockNote.
import './PageEditor.css';
import { AnchoredCompactToolbar } from './CompactSelectionToolbar';
// F 371 — the 6-dot block menu. Replaces BlockNote's default two-item
// menu (Delete, Colors) with the Notion set. The override point is the
// documented `dragHandleMenu` prop on `SideMenuProps`.
import { RichDragHandleMenu } from '../common/RichDragHandleMenu';
import { SideMenu, SideMenuController, SuggestionMenuController } from '@blocknote/react';
// F 392 — the shipped slash-menu builder, so the database entries are ADDED to
// the built-ins rather than replacing them.
import { getDefaultReactSlashMenuItems } from '@blocknote/react';
// F 371 — the editor-chrome context. It carries the page id, the move
// targets and the move itself down to the floating toolbar, which lives
// four levels inside BlockNote's own tree.
import { EditorChromeContext } from './EditorChromeContext';
import { BlockSealController } from '../common/BlockSealController';
import { EntityDetailFooter } from './EntityDetailFooter';
import { useTheme } from '../../themes';
import {
  savePageContent, loadPageContent, deletePageContent,
} from '../../../core/workspace/pageContentStorage';
import { mergeSealedBlocks } from '../../../core/workspace/mergeSealedBlocks';

/**
 * PageEditor — renders a single page in the workspace.
 *
 * Recipe: surface.page-editor
 * Behaviours: load-content, save-content, edit-title, delete-page, back
 *
 * ModeTheme: all colors via FreshCards --fc-* CSS vars. No useMantineColorScheme
 * race. No hardcoded hex/rgb.
 */

interface PageEditorProps {
  pageId?: string;
  peekMode?: boolean;
  className?: string;
}

const CSS = {
  // F 229 — the reading measure. Notion caps around 900px; measured in
  // production at 1920x1080 the editor body was 1200px wide with no cap, and
  // `CSS.body` did not exist at all, so `style={CSS.body}` rendered no
  // style — the sprawl was an unstyled element, not a missing max-width.
  //
  // One value, shared by header, toolbar and body. Declared here rather
  // than in the stylesheet because the inline `style` prop wins over a
  // class, and putting a max-width in CSS that the element can override
  // with an inline object would be a rule that only sometimes applies.
  column: {
    width: '100%',
    maxWidth: 900,
    margin: '0 auto',
    padding: '0 var(--fc-space-lg, 24px)',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    width: '100%',
    maxWidth: 900,
    margin: '0 auto',
    padding: 'var(--fc-space-md, 16px) var(--fc-space-lg, 24px) var(--fc-space-xl, 48px)',
  },
  wrapper: { height: '100%', display: 'flex', flexDirection: 'column' as const, backgroundColor: 'var(--fc-bg-primary)', color: 'var(--fc-text-primary)' },
  header: { borderBottom: '1px solid var(--fc-border-primary)', backgroundColor: 'var(--fc-bg-primary)', padding: 'var(--fc-space-sm, 8px) 0' },
  toolbar: { display: 'flex', gap: 4, borderBottom: '1px solid var(--fc-border-tertiary, #f0f0f0)', backgroundColor: 'var(--fc-bg-secondary, #fafafa)', padding: 'var(--fc-space-xs, 4px) 0' },
  // F 196A — the editor was a 32px "thin bar" in a 900px viewport.
  //
  // MEASURED, not assumed. Ancestor chain from ProseMirror upward, 1440x900:
  //   32 -> 32 -> 80 -> 172 -> 220 -> 268 -> 583 -> 850 -> 900 (100vh)
  //
  // Root cause: the chain from this div upward never resolves to a DEFINITE
  // height. It tops out at a `min-height: 100vh` wrapper, so every
  // `height: 100%` between is a percentage of an indefinite height and
  // computes to `auto`. A flex item with `flex: 1` inside an auto-height
  // column therefore sizes to its CONTENT — one line, for an empty doc.
  //
  // Two measured facts that cost most of the debugging time and are worth
  // recording, because they make the obvious fixes silently do nothing:
  //
  //   1. `min-height: 100%` on a child of a `min-height`-sized parent does
  //      NOT work. A percentage min-height still needs a DEFINITE parent
  //      height, and `min-height` does not confer one. Measured: a pixel
  //      `min-height:400px` on `.bn-container` gave 400px; `min-height:100%`
  //      on the same element gave 63px. Identical specificity, inline style.
  //   2. `height:100%` on `.bn-container` did nothing even applied as an
  //      inline style with the parent at a measured 640px — same reason.
  //
  // So the min-height has to be in VIEWPORT units, which are always definite,
  // and it has to land on the two elements BlockNote actually renders. The
  // class `fc-page-editor` scopes it to THIS component: `.ProseMirror` and
  // `.bn-container` are BlockNote's own class names used by DatabaseView too,
  // and this rule must not change how a database's editor lays out.
  //
  // Verified in-browser before writing: ProseMirror 63px -> 560px, and the
  // page fills the viewport.

  iconBtn: { color: 'var(--fc-text-secondary, #525252)' },
  titleInput: { fontSize: 18, fontWeight: 600, color: 'var(--fc-text-primary)' },
  muted: { color: 'var(--fc-text-hint, #8e8e8e)' },
};

export const PageEditor: React.FC<PageEditorProps> = ({
  pageId: overridePageId,
  peekMode = false,
  className = '',
}) => {
  const workspace = useWorkspace();
  const { modeName } = useTheme();
  const isDark = modeName === 'dark';
  const effectivePageId = overridePageId ?? workspace.currentPageId;

  const [editor, setEditor] = useState<FCCustomSchemaEditor | null>(null);

  // F 371 — the pages a block can be moved to. Every page in the workspace
  // EXCEPT the one being edited. The list comes from the workspace
  // context's own `pages`, so there is no second source of truth for "what
  // pages exist".
  const moveTargets = useMemo(
    () =>
      workspace.pages
        .filter((p) => p.id !== effectivePageId)
        .map((p) => ({ id: p.id, title: p.title || 'Untitled' })),
    [workspace.pages, effectivePageId],
  );

  /**
   * F 371 — move the given block(s) to another page.
   *
   * `moveBlockToPage` removes the block from THIS page's storage and
   * appends it to the destination's. The editor is then told to drop it
   * too, so the in-memory document and the stored document agree. The
   * editor's own autosave (500ms debounce) then rewrites the source page
   * from `editor.document`, which no longer contains the moved block.
   *
   * The destination is written first inside `moveBlockToPage` — see that
   * module's header for why the order is that way.
   */
  const handleMoveBlockToPage = useCallback(
    (destinationPageId: string, blockIds: string | string[]) => {
      if (!effectivePageId || !editor) return;
      const ids = Array.isArray(blockIds) ? blockIds : [blockIds];
      if (ids.length === 0) return;
      let moved = 0;
      for (const id of ids) {
        const result = moveBlockToPage(effectivePageId, destinationPageId, id);
        if (result.ok) moved += 1;
      }
      if (moved === 0) {
        setError('Could not move the block.');
        return;
      }
      // Drop them from the live editor so the two agree.
      try {
        editor.removeBlocks(ids);
      } catch {
        /* the block may already be gone; the storage write above stands */
      }
    },
    [effectivePageId, editor],
  );

  /**
   * F 429 — refresh the editor document after encrypt/decrypt.
   *
   * The encrypted-block module has already written the new Block[] to
   * `freshcards:content:<pageId>` and the editor's `onChange` triggers a
   * `savePageContent` debounce — but BlockNote's in-memory `editor.document`
   * has the OLD block shape. We swap the block in place by reading the
   * latest `Block[]` and finding the matching id, so BlockNote re-renders
   * the stub or the restored text without a full document reload.
   *
   * The shape change comes from storage — not from us — because the
   * encryption module is the only writer that touches the block. Re-deriving
   * from storage keeps the editor consistent with what was actually persisted.
   */
  const handleEncryptedBlockChange = useCallback(
    (_blockId: string, _action: 'encrypt' | 'decrypt') => {
      if (!editor || !effectivePageId) return;
      try {
        const blocks = loadPageContent(effectivePageId);
        if (!Array.isArray(blocks) || blocks.length === 0) return;
        // F 487 — STORAGE IS THE AUTHORITY, AND IT IS WRITTEN FIRST.
        //
        // The engine (`encryptBlock` / `decryptBlock`) already wrote the
        // sealed stub — or the restored plaintext block — to the page's
        // `Block[]`. That write is the one that carries the seal's
        // metadata: `attributes.encrypted`, `attributes.anchor` and the
        // `anchors` array.
        //
        // The editor cannot carry that metadata. BlockNote's paragraph block
        // spec declares no field for it, so `blockToNode` DROPS it on the way
        // into ProseMirror and `nodeToBlock` cannot invent it back. The
        // editor's copy of a sealed block is a paragraph that says
        // "🔒 [encrypted block]" and nothing else.
        //
        // So: write the STORED blocks to storage unchanged (they are
        // already correct), and update the editor only so the operator SEES
        // the change. The editor's lossy copy is never written back — the
        // autosave's `mergeSealedBlocks` makes sure of that.
        savePageContent(effectivePageId, blocks);
        // Replace the whole document so the operator sees the sealed stub
        // (or the restored text) without a full document reload.
        editor.replaceBlocks(editor.document, blocks as unknown[]);
      } catch {
        /* the editor may be torn down — storage already won */
      }
    },
    [editor, effectivePageId],
  );

  // F 232 — attach the code chrome once the ProseMirror view exists.
  // BlockNoteView creates the view asynchronously, so a one-shot read of
  // `editor.view` on mount finds nothing and leaves the chrome detached
  // with no error anywhere. Poll briefly, then give up loudly.
  const chromeHostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!editor || !chromeHostRef.current) return;
    const host = chromeHostRef.current;
    let ctl: { destroy: () => void } | null = null;
    let tries = 0;
    const timer = setInterval(() => {
      // The accessor is `prosemirrorView`, not `view`. Measured: with
      // `view` the poll ran to timeout on a page where ProseMirror was
      // plainly in the DOM — a real API miss, not a race.
      const view = (editor as unknown as { prosemirrorView?: import('@tiptap/pm/view').EditorView }).prosemirrorView;
      if (view?.dom) {
        clearInterval(timer);
        ctl = attachCodeChrome(view, host);
      } else if (++tries > 60) {
        clearInterval(timer);
        console.warn('[f232] ProseMirror view never appeared; code chrome not attached');
      }
    }, 100);
    return () => { clearInterval(timer); ctl?.destroy(); };
  }, [editor]);
  const [title, setTitle] = useState('Untitled');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // F 154 C1 — raw markdown toggle. PER-EDITOR-INSTANCE by construction: this
  // is component state on PageEditor, not anything global, so two editors on
  // one page can never fight over the mode.
  const [isRawMode, setIsRawMode] = useState(false);

  const editorRef = useRef<BlockNoteEditor | null>(null);
  const initialContentRef = useRef<any[] | null>(null);

  // Resolve page metadata whenever effectivePageId changes
  const pageMeta = useMemo(() => {
    if (!effectivePageId) return null;
    if (workspace.currentPage?.id === effectivePageId) return workspace.currentPage;
    return workspace.getPageById(effectivePageId) ?? null;
  }, [effectivePageId, workspace.currentPage, workspace]);

  // Behaviour: load-content
  useEffect(() => {
    let cancelled = false;
    if (!effectivePageId) {
      setEditor(null);
      return;
    }
    setTitle(pageMeta?.title ?? 'Untitled');
    setIsLoading(true);
    try {
      const content = loadPageContent(effectivePageId);
      // F 427 — an empty page seeds ONE EMPTY PARAGRAPH, not one paragraph
      // containing the words "Start writing here…".
      //
      // The old seed made the prompt REAL CONTENT: a text node in the document.
      // A placeholder is not content, so it cannot overlap anything — but real
      // content sits above the caret and the operator's typing was appended
      // AFTER it, which is the overlap they reported. Worse, the seeded block
      // was then persisted by the autosave on first edit, so the prompt became
      // permanently part of the page.
      //
      // BlockNote already renders "Start writing here…" itself, via
      // `placeholders.emptyDocument`, configured 70 lines below in this same
      // file. Seeding an empty document is therefore what lets that placeholder
      // do its job: it vanishes on focus, and it is never saved.
      const blocks = content && content.length > 0 ? content : [
        { type: 'paragraph' },
      ];
      initialContentRef.current = blocks;
      if (cancelled) return;
      // uploadFile handles drag-and-drop, paste, and slash-menu file/image
      // uploads. We keep files as base64 data URLs in localStorage so the
      // editor works fully offline. Files > 4MB are rejected with a toast.
      const ed = BlockNoteEditor.create({
        initialContent: blocks,
        // F 217 — neighbour-aware block spacing. Supplies data-prev-kind
        // and data-fc-gap to each blockContainer; the DECISION is in
        // src/core/editor/blockSpacing.ts (pure, unit-tested) and the
        // VALUE is applied by PageEditor.css. See
        // neighbourSpacingExtension.ts for why this is an Extension and
        // not a component: BlockNote 0.47.3 owns block rendering and
        // exposes no React hook for it.
        extensions: [neighbourSpacing({})],
        // F 232 — code block chrome + syntax highlighting. Stage 1 measured
        // the shipped block: no language picker (zero <select> in the
        // document) and a single flat text colour. Both are configured
        // here.
        //
        // `createHighlighter` returns the Shiki instance ITSELF, not a
        // facade: BlockNote calls `getLoadedLanguages()` and, for a
        // grammar it has not seen, `loadLanguage()` on whatever this
        // returns, then builds its own ProseMirror parser from it. A
        // `{codeToHtml}` wrapper satisfies the type and fails silently.
        // The option is `schema`, not `codeBlock` — passing `codeBlock`
        // to `BlockNoteEditor.create` is silently ignored, which is why
        // the first two attempts produced no picker and no highlighting
        // with no error. And `blockSpecs` REPLACES rather than merges, so
        // the defaults are spread in explicitly: passing only codeBlock
        // would have left the editor with one block type.
        schema: BlockNoteSchema.create({
          blockSpecs: {
            ...defaultBlockSpecs,
            codeBlock: createCodeBlockSpec({
              defaultLanguage: DEFAULT_LANGUAGE,
              supportedLanguages: SUPPORTED_LANGUAGES,
              createHighlighter: () => getCodeHighlighter() as any,
            }),
            // F 392 — a database embedded IN a page. Registered here (not in
            // the common BlockNoteEditor wrapper) for the same reason as
            // codeBlock: that wrapper is not mounted anywhere, so this is the
            // only call site that builds a real editor. Same rule about
            // spreading defaultBlockSpecs applies — blockSpecs REPLACES
            // rather than merges.
            inlineDatabase: inlineDatabaseBlock,
          },
        }),
        // F 202 B — the placeholder is set through the editor's DICTIONARY,
        // not per-block. `placeholders` is deprecated in 0.47 and the
        // documented route is `dictionary` (BlockNoteEditor.ts:104, and
        // :420-423 merges dictionary.placeholders into the resolved set).
        //
        // Why the dictionary and not CSS: the placeholder is injected as a
        // generated `content:` rule on a per-mount class
        // (Placeholder.ts: `uniqueEditorSelector`), so a stylesheet override
        // would have to out-specify a rule the editor writes itself into a
        // <style> it owns. Setting it at the source covers every empty block
        // in every page, which is what the dispatch asked for — NOT a
        // single page's saved content.
        // F 202 B — set BOTH. Measured on production: passing ONLY
        // `dictionary` produced ZERO injected content rules, i.e. the
        // placeholder vanished entirely rather than changing. That option is
        // the documented route in 0.47, but Placeholder.ts reads
        // `options.placeholders`, so that is set explicitly too. Keeping the
        // dictionary spread means nothing else in the i18n bundle is lost.
        placeholders: {
          ...en.placeholders,
          default: "Start writing here…",
          emptyDocument: "Start writing here…",
        },
        dictionary: {
          ...en,
          placeholders: {
            ...en.placeholders,
            default: "Start writing here…",
            emptyDocument: "Start writing here…",
          },
        },
        uploadFile: async (file: File): Promise<string> => {
          if (!file.type.startsWith('image/')) {
            throw new Error('Only image files are supported for upload.');
          }
          if (file.size > 4 * 1024 * 1024) {
            throw new Error('Image is over 4MB. Use a smaller image or a hosted URL.');
          }
          return await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
          });
        },
      });
      // F 392 — `ed` is typed against the DEFAULT block schema + our two
      // custom specs; the state is declared as the default-schema editor,
      // because every other member used below (`replaceBlocks`,
      // `removeBlocks`, `document`) exists on both and retyping the file for
      // a schema widening is out of scope. The runtime object is identical
      // apart from the extra block type — this narrows the STATIC type only.
      editorRef.current = ed as FCCustomSchemaEditor;
      setEditor(ed as FCCustomSchemaEditor);
      setIsDirty(false);
    } catch (e) {
      console.error('[PageEditor] Failed to load content:', e);
      setError('Failed to load page content');
    } finally {
      setIsLoading(false);
    }
    return () => { cancelled = true; };
  }, [effectivePageId, pageMeta?.title]);

  // Behaviour: enter raw mode. Project the CURRENT canonical blocks, not the
  // last-saved copy — otherwise a toggle would silently discard unsaved edits.
  const openRaw = useCallback(() => {
    if (!editor) return;
    setIsRawMode(true);
  }, [editor]);

  // Behaviour: commit raw markdown back into the canonical blocks.
  const commitRaw = useCallback((nextBlocks: unknown[]) => {
    if (!editor) return;
    // Replace the whole document, then let the existing onChange autosave
    // handler persist it. Going through the editor (rather than writing
    // storage directly) keeps one save path instead of two.
    editor.replaceBlocks(editor.document, nextBlocks as any[]);
    setIsRawMode(false);
  }, [editor]);

  // Behaviour: paste normalisation (F 154 C2)
  usePasteNormalisation({ editor });

  // Behaviour: save-content (auto-save on every change, debounced)
  useEffect(() => {
    if (!editor || !effectivePageId) return;
    const handler = () => {
      try {
        // F 487 — a sealed block keeps the STORED form.
        //
        // The editor cannot represent a sealed block faithfully: BlockNote's
        // paragraph spec has no field for `attributes.encrypted`,
        // `attributes.anchor` or the `anchors` array, so the editor's copy
        // of a sealed block is a paragraph that merely SAYS "🔒 [encrypted
        // block]" and carries none of the seal's metadata. Saving it would
        // replace the sealed block with an unsealed one and destroy the
        // public anchor — the operator's semantic tag.
        //
        // Measured on production, against the graph: the sealed block's
        // `encrypted` flag and `anchor` were present in storage for a page
        // sealed through the drag-handle menu, and ABSENT for a page sealed
        // through the same code path in a different session. The difference
        // was the editor's autosave, not the seal.
        //
        // So the editor's document is the base, and the STORED content wins
        // for every block that is sealed. See `mergeSealedBlocks.ts`.
        const blocks = mergeSealedBlocks(editor.document, loadPageContent(effectivePageId));
        savePageContent(effectivePageId, blocks);
        setIsDirty(false);
        setLastSavedAt(new Date());
      } catch (e) {
        console.error('[PageEditor] Auto-save failed:', e);
        setError('Auto-save failed');
      }
    };
    // Debounce: 500ms after the last change
    let timer: number | undefined;
    const debounced = () => {
      if (timer) clearTimeout(timer);
      timer = window.setTimeout(handler, 500);
      setIsDirty(true);
    };
    const unsubscribe = editor.onChange(debounced);
    return () => {
      unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, [editor, effectivePageId]);

  // Behaviour: edit-title
  const startEditingTitle = useCallback(() => {
    if (peekMode) return;
    setIsEditingTitle(true);
  }, [peekMode]);

  const commitTitle = useCallback(() => {
    if (!effectivePageId) return;
    const trimmed = title.trim() || 'Untitled';
    setTitle(trimmed);
    setIsEditingTitle(false);
    if (trimmed !== pageMeta?.title) {
      workspace.updatePage(effectivePageId, { title: trimmed });
    }
  }, [effectivePageId, title, pageMeta?.title, workspace]);

  // Behaviour: delete-page
  const handleDelete = useCallback(() => {
    if (!effectivePageId) return;
    if (!confirm('Delete this page? This cannot be undone.')) return;
    deletePageContent(effectivePageId);
    workspace.deletePage(effectivePageId);
  }, [effectivePageId, workspace]);

  // Behaviour: back
  const handleBack = useCallback(() => {
    if (workspace.canGoBack) workspace.goBack();
    else workspace.goToHome();
  }, [workspace]);

  /**
   * F 371 — turn the block at the text cursor into a heading.
   *
   * `editor.toggleHeading({level})` does not exist in BlockNote 0.47.3
   * (measured: `TypeError: c.toggleBold is not a function` on the same
   * toolbar). The real API is `updateBlock`, and the target is the block
   * the text cursor is in.
   */
  const toggleHeading = useCallback(
    (level: 1 | 2) => {
      if (!editor) return;
      const cursor = editor.getTextCursorPosition();
      if (!cursor?.block) return;
      editor.focus();
      editor.updateBlock(cursor.block, {
        type: 'heading',
        props: { level },
      });
    },
    [editor],
  );

  // Reset error after a moment
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 3000);
    return () => clearTimeout(t);
  }, [error]);

  if (!effectivePageId) {
    return (
      <Box className={`fc-page-editor ${className}`} style={CSS.wrapper}>
        <Box p="xl" style={{ textAlign: 'center', ...CSS.muted }}>
          <Text>No page selected.</Text>
        </Box>
      </Box>
    );
  }

  return (
    /* F 487 — the seal-dialog owner wraps the whole editor.
     *
     * It has to wrap BOTH the editor and the dialog: the dialog's `opened`
     * state must live outside BlockNote's side-menu tree (that tree is
     * unmounted by the click that opens the dialog), and the menu that
     * OPENS the dialog lives inside that tree — so a context only reaches
     * it if the provider is an ancestor of the editor.
     *
     * The controller itself is NOT inside `<BlockNoteView>`, so clicking a
     * menu item — which closes the menu and unmounts BlockNote's side-menu
     * subtree — cannot destroy the dialog's state. That was F 429's bug,
     * measured on production: the menu item was gone from the DOM 30ms
     * after the click and the dialog never appeared.
     */
    <BlockSealController
      pageId={effectivePageId}
      onSealedBlockChange={handleEncryptedBlockChange}
    >
    <Box className={`fc-page-editor ${className}`} style={CSS.wrapper}>
      {!peekMode && (
        <Box style={CSS.header}>
          {/* F 229 — 3f: the title row aligns to the content column. */}
          <Box style={CSS.column}>
          <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
            <Group gap="xs" align="center" style={{ minWidth: 0, flex: 1 }}>
              <ActionIcon
                variant="subtle"
                size="md"
                onClick={handleBack}
                title={workspace.canGoBack ? 'Back to previous page' : 'Back to home'}
                aria-label="Back"
                style={{ flexShrink: 0 }}
              >
                <IconArrowLeft size={18} />
              </ActionIcon>
              <IconFileText size={18} style={{ ...CSS.muted, flexShrink: 0 }} />
              {isEditingTitle ? (
                <TextInput
                  value={title}
                  onChange={(e) => setTitle(e.currentTarget.value)}
                  onBlur={commitTitle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.currentTarget.blur(); }
                    if (e.key === 'Escape') { setTitle(pageMeta?.title ?? 'Untitled'); setIsEditingTitle(false); }
                  }}
                  variant="filled"
                  size="sm"
                  autoFocus
                  styles={{ input: CSS.titleInput }}
                  style={{ flex: 1, minWidth: 0 }}
                />
              ) : (
                <Text
                  size="md"
                  fw={600}
                  onClick={startEditingTitle}
                  style={{ cursor: 'pointer', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {title}
                </Text>
              )}
              {isDirty && <Text size="xs" style={CSS.muted}>●</Text>}
              {!isDirty && lastSavedAt && <Text size="xs" style={CSS.muted}><IconCircleCheck size={12} style={{ verticalAlign: 'middle' }} /> saved</Text>}
            </Group>
            <Group gap="xs" style={{ flexShrink: 0 }}>
              <ActionIcon
                variant="subtle"
                color="red"
                size="md"
                onClick={handleDelete}
                title="Delete page"
                aria-label="Delete page"
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Group>
          </Group>
          </Box>
        </Box>
      )}

      {/* Minimal editor toolbar (only when editor is ready) */}
      {editor && (
        <Box style={CSS.toolbar}>
          <Box style={CSS.column}>
          {/* F 371 — these four buttons were BROKEN before this dispatch and
              were not in its scope. Measured on production in real Chromium:
              clicking Bold threw `TypeError: c.toggleBold is not a function`.
              The cause is an API that does not exist in BlockNote 0.47.3 —
              `toggleBold`, `toggleItalic` and `toggleHeading` are not on
              `BlockNoteEditor`; the real API is `toggleStyles({bold: true})`
              for text styles and `updateBlock(block, {type, props})` for a
              block type.

              It is fixed here because F 371's "Turn into (H1/H2/H3)" needs
              the heading half of exactly this API, and shipping a new
              heading control next to a broken one would be worse than
              fixing it. The visible design is unchanged: same four
              buttons, same icons, same titles. */}
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => {
              editor.focus();
              editor.toggleStyles({ bold: true } as never);
            }}
            title="Bold (⌘B)"
            aria-label="Bold"
            className="fc-page-editor__toolbar-btn"
          ><IconBold size={16} /></ActionIcon>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => {
              editor.focus();
              editor.toggleStyles({ italic: true } as never);
            }}
            title="Italic (⌘I)"
            aria-label="Italic"
            className="fc-page-editor__toolbar-btn"
          ><IconItalic size={16} /></ActionIcon>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => toggleHeading(1)}
            title="Heading 1"
            aria-label="Heading 1"
            className="fc-page-editor__toolbar-btn"
          ><IconH1 size={16} /></ActionIcon>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => toggleHeading(2)}
            title="Heading 2"
            aria-label="Heading 2"
            className="fc-page-editor__toolbar-btn"
          ><IconH2 size={16} /></ActionIcon>
          {/* F 154 C1 — rich text <-> raw markdown */}
          <ActionIcon
            variant={isRawMode ? 'filled' : 'subtle'}
            size="sm"
            onClick={() => (isRawMode ? setIsRawMode(false) : openRaw())}
            title={isRawMode ? 'Back to rich text' : 'Edit as raw markdown'}
            aria-label={isRawMode ? 'Back to rich text' : 'Edit as raw markdown'}
            data-testid="fc-raw-toggle"
            className="fc-page-editor__toolbar-btn"
          >
            {isRawMode ? <IconBold size={16} /> : <IconCode size={16} />}
          </ActionIcon>
          </Box>
        </Box>
      )}

      <Box style={CSS.body}>
        {error && (
          <Notification color="red" onClose={() => setError(null)} mb="md">
            {error}
          </Notification>
        )}
        {isLoading && <Text style={CSS.muted}>Loading…</Text>}
        {editor && (
          /* F 154 C1 — raw markdown REPLACES the rich view rather than nesting
             inside it. Two editable surfaces for one document would be two
             sources of truth; the toggle swaps which one owns the content. */
          isRawMode ? (
            <RawMarkdownEditor
              blocks={editor.document}
              onCommit={commitRaw}
              onCancel={() => setIsRawMode(false)}
            />
          ) : (
            <div ref={chromeHostRef} data-fc-code-host="">
            {/* F 371 — the editor-chrome context. It gives the floating
                toolbar the page id, the move targets and the move itself,
                without changing the controller's pinned source form. */}
            <EditorChromeContext.Provider
              value={{
                pageId: effectivePageId,
                moveTargets,
                onMoveTo: handleMoveBlockToPage,
              }}
            >
            <BlockNoteView
              editor={editor}
              theme={isDark ? 'dark' : 'light'}
              // F 200 Bug F — turn OFF BlockNote's default 12-control
              // formatting toolbar, then supply the 4-control compact one as
              // a child.
              //
              // Both halves are required. BlockNoteViewEditor renders
              // `<BlockNoteDefaultUI {...defaultUIProps} />` and THEN
              // `{props.children}` — children ADD a toolbar, they do not
              // replace one. Measured on production after passing only
              // children: 15 buttons (12 default + 4 mine, with Bold/Italic/
              // Underline/Link each appearing twice). With
              // formattingToolbar:false the count is exactly 4.
              formattingToolbar={false}
              // F 371 — same two halves, same reason, for the SIDE MENU.
              // `sideMenu={false}` suppresses BlockNote's default
              // `SideMenuController` (whose 6-dot menu was measured on
              // production with exactly two items: "Delete" and "Colors"),
              // and the child controller below supplies our own.
              sideMenu={false}
            >
              {/* F 202 C — the CONTROLLER does the anchoring; CompactSelectionToolbar
                  as a bare child did not (toolbar landed 558px from the selection,
                  pinned to the panel's bottom-left). The controller is exported and
                  takes a `formattingToolbar` prop, so hand it the 4-button
                  component and keep positioning. `formattingToolbar={false}` above
                  still suppresses the default 12-item toolbar.
                  F 371 keeps this EXACT form: the toolbar's host data
                  (page, move targets) comes from the context above, so the
                  pinned shape is not disturbed. */}
              <AnchoredCompactToolbar />
              {/* F 371 — the 6-dot block menu. `SideMenu` is BlockNote's own
                  component; the only thing changed is WHICH menu its
                  drag handle opens (`dragHandleMenu`), which is the
                  documented `SideMenuProps` override point. BlockNote still
                  owns the button, the drag, the positioning and the
                  focus freeze. */}
              {/* F 392 — `/database` in the slash menu.

                  MEASURED, because the obvious route does not exist:
                  `BlockNoteView` has NO `suggestions` prop — passing one is a
                  TS2322 reading "Property 'suggestions' does not exist on
                  type ..." (node_modules/@blocknote/mantine/src/
                  BlockNoteView.tsx forwards only ComponentProps of
                  BlockNoteViewRaw).

                  The real seam is `SuggestionMenuController`, whose documented
                  props are `triggerCharacter` + `getItems`
                  (node_modules/@blocknote/react/types/src/components/
                  SuggestionMenu/SuggestionMenuController.d.ts:7). It is a
                  CHILD of BlockNoteView, exactly like SideMenuController.

                  It REPLACES the default slash menu, so `getItems` must
                  re-emit the built-ins too — measured here rather than
                  assumed, because returning only the database rows would
                  silently delete Heading/Code/Table from the menu. */}
              <SuggestionMenuController
                triggerCharacter="/"
                getItems={async () => [
                  ...getDefaultReactSlashMenuItems(editor),
                  ...getInlineDatabaseSlashMenuItems(editor),
                  // F 487 — the "Encrypted Block" slash entry. It calls the
                  // SAME seal dialog as the drag-handle "Turn into" item,
                  // through `BlockSealController`, which is mounted outside
                  // `<BlockNoteView>` — so the slash menu closing cannot
                  // destroy the dialog's state either.
                  ...getEncryptedBlockSlashMenuItems(editor),
                ]}
              />
              <SideMenuController
                sideMenu={() => (
                  <SideMenu
                    dragHandleMenu={() => (
                      <RichDragHandleMenu
                        moveTargets={moveTargets}
                        onMoveTo={handleMoveBlockToPage}
                        pageId={effectivePageId}
                        onEncryptedBlockChange={handleEncryptedBlockChange}
                      />
                    )}
                  />
                )}
              />
            </BlockNoteView>
            </EditorChromeContext.Provider>
            </div>
          )
        )}

        {/* F 487 — the seal dialog's owner is the `BlockSealController` that
            WRAPS this whole tree (see the return above). It is deliberately
            not mounted here: a controller rendered as a sibling of the
            editor would provide its context to nothing, because the menu
            that opens the dialog lives inside `<BlockNoteView>`. */}

        {/* F 228 — History and Used in. Collapsed by default, below the
            document. F 227 shipped both read helpers with no UI; this is
            where they become visible. It reads them and changes neither. */}
        <EntityDetailFooter entityId={pageMeta?.id ?? null} />
      </Box>
    </Box>
    </BlockSealController>
  );
};

export default PageEditor;
